import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { notificationEnvelopeSchema } from "@guestbook/shared";
import { useMe } from "@/shared/lib/auth";
import { useToast } from "@/shared/components/ui/Toast";

const STREAM_URL = "/api/v1/notifications/stream";

/** Server pings ~every 20s; ~3 missed pings means the connection is dead. */
const STALE_AFTER_MS = 70_000;
/** How often the watchdog checks for a silently-dead connection. */
const WATCHDOG_INTERVAL_MS = 15_000;

/**
 * Subscribe to the in-app notification SSE stream for the duration of an authed
 * session. On a `submission_received` event, refresh the submissions list and
 * pop a toast. Mount once in the authed shell.
 *
 * Cleanup on both ends: the server tears down on socket close + heartbeat-write
 * failure; here we close on unmount and a watchdog force-reconnects a connection
 * that's gone quiet (EventSource auto-reconnects on transport drops, but not on
 * a proxy that holds a dead socket open without sending bytes).
 */
export function useNotifications(): void {
  const qc = useQueryClient();
  const { toast } = useToast();
  const me = useMe();
  const userId = me.data?.id;

  useEffect(() => {
    // No user, or an environment without EventSource (jsdom in tests that don't
    // stub it) — nothing to subscribe to.
    if (!userId || typeof EventSource === "undefined") return;

    let source: EventSource | null = null;
    let lastMessageAt = Date.now();

    const handleSubmission = (event: Event): void => {
      lastMessageAt = Date.now();
      let data: unknown;
      try {
        data = JSON.parse((event as MessageEvent).data);
      } catch {
        return;
      }
      const result = notificationEnvelopeSchema.safeParse(data);
      if (!result.success || result.data.type !== "submission_received") return;

      qc.invalidateQueries({ queryKey: ["submissions", "list"] });
      const { visitorName } = result.data;
      toast({
        title: "New submission",
        description: visitorName
          ? `${visitorName} shared their details`
          : "Someone shared their details",
      });
    };

    const open = (): void => {
      const es = new EventSource(STREAM_URL, { withCredentials: true });
      source = es;
      lastMessageAt = Date.now();
      es.addEventListener("ping", () => {
        lastMessageAt = Date.now();
      });
      es.addEventListener("submission_received", handleSubmission);
    };

    open();

    const watchdog = setInterval(() => {
      if (Date.now() - lastMessageAt > STALE_AFTER_MS) {
        lastMessageAt = Date.now();
        source?.close();
        open();
      }
    }, WATCHDOG_INTERVAL_MS);

    return () => {
      clearInterval(watchdog);
      source?.close();
    };
  }, [userId, qc, toast]);
}
